import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertCircle } from 'lucide-react';
import { SignInLockedPanel } from '@/components/auth/SignInLockedPanel';
import { classifyLoginError } from '@/lib/loginErrors';

// Phase 4B-3b — dedicated two-factor login challenge step. The restricted
// challenge token exists only in React state; refresh/back discards it and no
// portal shell or API access is possible until verification succeeds.
function TwoFactorChallenge({ challenge, onSuccess, onBack }) {
  const { completeTwoFactor } = useAuth();
  const [method, setMethod] = useState('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(challenge.expiresInSeconds || 300);
  const [terminal, setTerminal] = useState(null); // 'expired' | 'locked'

  useEffect(() => {
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (secondsLeft === 0 && !terminal) setTerminal('expired'); }, [secondsLeft, terminal]);

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeTwoFactor(challenge.challengeToken, method, code);
      onSuccess();
    } catch (err) {
      const data = err.response?.data;
      const errObj = data?.error || data?.detail?.error;
      const errCode = errObj?.code;
      const msg = errObj?.message || 'Verification failed. Please try again.';
      if (errCode === 'CHALLENGE_LOCKED') setTerminal('locked');
      else if (errCode === 'CHALLENGE_EXPIRED' || errCode === 'CHALLENGE_INVALID' || errCode === 'CHALLENGE_USED') setTerminal('expired');
      else setError(msg);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(1, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  if (terminal) {
    return (
      <div className="space-y-4 text-center" data-testid="2fa-challenge-terminal">
        <Alert variant="destructive" data-testid="2fa-terminal-message">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {terminal === 'locked'
              ? 'Too many incorrect codes were entered. For security, this sign-in attempt was locked.'
              : 'This sign-in challenge has expired or is no longer valid.'}
            {' '}Please sign in again.
          </AlertDescription>
        </Alert>
        <Button className="w-full h-11" onClick={onBack} data-testid="2fa-return-to-login">Return to sign in</Button>
      </div>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-4" data-testid="2fa-challenge-form">
      {error && (
        <Alert variant="destructive" data-testid="2fa-challenge-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <p className="text-sm text-muted-foreground" data-testid="2fa-challenge-instructions">
        {method === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Enter one of your saved recovery codes. Each recovery code works exactly once.'}
      </p>
      <div className="space-y-2">
        <Label htmlFor="2fa-code">{method === 'totp' ? 'Authenticator code' : 'Recovery code'}</Label>
        <Input
          id="2fa-code"
          autoFocus
          inputMode={method === 'totp' ? 'numeric' : 'text'}
          autoComplete="one-time-code"
          maxLength={method === 'totp' ? 6 : 12}
          placeholder={method === 'totp' ? '123456' : 'XXXXX-XXXXX'}
          value={code}
          onChange={(e) => setCode(method === 'totp' ? e.target.value.replace(/\D/g, '') : e.target.value.toUpperCase())}
          required
          data-testid="2fa-code-input"
          className="h-11 tracking-widest"
        />
      </div>
      <Button type="submit" className="w-full h-11" disabled={loading || code.length < 6} data-testid="2fa-verify-button">
        {loading ? 'Verifying...' : 'Verify'}
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" className="text-primary hover:underline"
                onClick={() => { setMethod(method === 'totp' ? 'recovery_code' : 'totp'); setCode(''); setError(''); }}
                data-testid="2fa-toggle-method">
          {method === 'totp' ? 'Use a recovery code instead' : 'Use an authenticator code instead'}
        </button>
        <span className="text-muted-foreground tabular-nums" aria-live="polite" data-testid="2fa-expiry-countdown">Expires in {mm}:{ss}</span>
      </div>
      <button type="button" className="block w-full text-center text-sm text-muted-foreground hover:text-primary"
              onClick={onBack} data-testid="2fa-back-to-login">Back to sign in</button>
    </form>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [lock, setLock] = useState(null); // AUTH-2: { retryAfterSeconds } while sign-in is temporarily locked
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const { login } = useAuth();
  const { organisationName, logoUrl, loginLogoUrl, primaryColour } = useBranding();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLock(null);
    setLoading(true);
    try {
      const result = await login(email, password, rememberMe);
      if (result?.twoFactorRequired) {
        setChallenge(result);
        return;
      }
      const isAdminUser = ['Super Admin', 'Admin', 'HR', 'Finance', 'Coordinator', 'Roster Manager'].includes(result.role_name);
      navigate(isAdminUser ? '/dashboard' : '/dashboard');
    } catch (err) {
      const classified = classifyLoginError(err);
      if (classified.kind === 'RATE_LIMITED') setLock({ retryAfterSeconds: classified.retryAfterSeconds });
      else setError(classified.message);
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => { setChallenge(null); setPassword(''); setError(''); setLock(null); };
  const clearLock = React.useCallback(() => setLock(null), []);

  return (
    <div className="min-h-screen flex">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[hsl(var(--primary)/1)] to-[hsl(var(--primary)/0.75)] items-center justify-center p-12">
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            {(loginLogoUrl || logoUrl) ? (
              <img src={loginLogoUrl || logoUrl} alt={organisationName} className="h-12 w-12 rounded-xl object-contain bg-white/20 p-1" data-testid="login-logo" />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
            )}
            <h1 className="text-3xl font-bold tracking-tight" data-testid="login-org-name">{organisationName}</h1>
          </div>
          <h2 className="text-2xl font-semibold mb-4">NDIS Provider Management</h2>
          <p className="text-white/80 text-lg leading-relaxed">
            Manage participants, staff, funding, rosters, and timesheets — all in one place.
            Built for care providers who need clarity and control.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4">
            {['Roster Management', 'Funding Tracking', 'GPS Check-in', 'Invoice Export'].map((f) => (
              <div key={f} className="bg-white/10 rounded-lg px-4 py-3 text-sm text-white/90">{f}</div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(800px_600px_at_30%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md flex flex-col gap-3">
        <Card className="w-full shadow-lg border-0 bg-card">
          <CardHeader className="space-y-2 text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              {(loginLogoUrl || logoUrl) ? (
                <img src={loginLogoUrl || logoUrl} alt={organisationName} className="h-10 w-10 rounded-lg object-contain" data-testid="login-logo-mobile" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary-foreground" />
                </div>
              )}
              <h1 className="text-xl font-bold" data-testid="login-org-name-mobile">{organisationName}</h1>
            </div>
            <CardTitle className="text-2xl">{challenge ? 'Two-factor verification' : 'Welcome back'}</CardTitle>
            <CardDescription>{challenge ? 'A second step is required to finish signing in' : 'Sign in to your account to continue'}</CardDescription>
          </CardHeader>
          <CardContent>
            {challenge ? (
              <TwoFactorChallenge
                challenge={challenge}
                onSuccess={() => navigate('/dashboard')}
                onBack={resetToLogin}
              />
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {lock && (
                <SignInLockedPanel retryAfterSeconds={lock.retryAfterSeconds} onExpire={clearLock} testId="login-rate-limited" />
              )}
              {error && !lock && (
                <Alert variant="destructive" data-testid="login-error-text">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@organisation.com.au"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-11"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(v) => setRememberMe(v === true)}
                  data-testid="login-remember-me"
                />
                <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer select-none">
                  Remember me for 7 days
                </Label>
              </div>
              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading || !!lock}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : (lock ? 'Sign-in temporarily locked' : 'Sign in')}
              </Button>
              <a href="/forgot-password" className="block text-center text-sm text-muted-foreground hover:text-primary">Forgot password?</a>
            </form>
            )}
          </CardContent>
        </Card>
        {/* Stabilisation Blocker #5 (Feb 2026) — single-tenant deployment banner. */}
        <div
          className="flex items-start gap-2 rounded-md border border-slate-200 bg-white/70 px-3 py-2 text-[11px] text-slate-600 shadow-sm"
          data-testid="single-tenant-banner"
          role="note"
        >
          <span aria-hidden className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
          <span className="leading-snug">
            <span className="font-semibold text-slate-700">Single-tenant deployment.</span>{' '}
            Data is scoped by branch and state. For multi-organisation rollout, see the V2 SaaS roadmap.
          </span>
        </div>
        </div>
      </div>
    </div>
  );
}
